/**
 * ZonesSection - Configure cold storage zones + display weather data
 */

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Alert, CircularProgress, Paper, Radio, RadioGroup, FormControlLabel, Link as MuiLink } from '@mui/material';
import { ElectricBolt, OpenInNew } from '@mui/icons-material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import { SectionCard, vortxColors, useThemeMode, vortxTypography } from '@vortx/shared-ui';
import ZoneEditor from '../ZoneEditor.jsx';
import { useWeatherData } from '../../hooks/useWeatherData';

export default function ZonesSection({ zones, updateZones, project, onUpdateLoadRelationship }) {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { mode } = useThemeMode();
    const isRetro = mode === 'retro';

    const lat = project?.location?.lat;
    const lon = project?.location?.lon;
    const { weatherData, loading: weatherLoading, error: weatherError } = useWeatherData(lat, lon);

    // Build chart data from design day temps
    const chartData = weatherData?.designDayTemps?.map((temp, i) => ({
        hour: `${i}:00`,
        temp,
    })) || [];

    return (
        <Box>
            {/* Meter info banner */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: vortxColors.charcoal, border: `1px solid ${vortxColors.teal}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ElectricBolt sx={{ color: '#2196F3', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: vortxColors.white }}>
                        Meter: <strong>{project?.meters?.main?.name || 'Facility Meter'}</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ color: vortxColors.stone, ml: 2 }}>
                        Tariff: {project?.meters?.main?.tariff?.name || 'Not set'}
                    </Typography>
                    <MuiLink
                        component="button"
                        onClick={() => navigate(`/all-projects/${projectId}#meters`)}
                        sx={{
                            ml: 'auto',
                            color: vortxColors.gold,
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        Configure in project settings
                        <OpenInNew sx={{ fontSize: 12 }} />
                    </MuiLink>
                </Box>
                <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" sx={{ color: vortxColors.stone, display: 'block', mb: 0.5 }}>
                        Cold storage load relationship:
                    </Typography>
                    <RadioGroup
                        row
                        value={project?.apps?.flux_cold?.load_relationship || 'included'}
                        onChange={(e) => onUpdateLoadRelationship && onUpdateLoadRelationship(e.target.value)}
                        sx={{ ml: 1 }}
                    >
                        <FormControlLabel
                            value="included"
                            control={<Radio size="small" sx={{ color: vortxColors.stone, '&.Mui-checked': { color: vortxColors.gold } }} />}
                            label={<Typography variant="caption" sx={{ color: vortxColors.white }}>Included in facility load</Typography>}
                        />
                        <FormControlLabel
                            value="additive"
                            control={<Radio size="small" sx={{ color: vortxColors.stone, '&.Mui-checked': { color: vortxColors.gold } }} />}
                            label={<Typography variant="caption" sx={{ color: vortxColors.white }}>Modeled separately</Typography>}
                        />
                    </RadioGroup>
                </Box>
            </Paper>

            <SectionCard
                title="Facility Zones"
                description="Configure refrigeration zones for your cold storage facility"
            >
                <Box sx={{ mb: 3 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Add zones to model different temperature-controlled areas in your facility.
                        Each zone can have different thermal properties and temperature requirements.
                    </Alert>
                    <ZoneEditor
                        zones={zones || []}
                        onChange={updateZones}
                        peakAmbientF={weatherData?.hourlyTemps ? Math.max(...weatherData.hourlyTemps) : null}
                    />
                </Box>
            </SectionCard>

            <SectionCard
                title="Ambient Conditions"
                description="Weather data for thermal load calculations"
                sx={{ mt: 3 }}
            >
                {(lat == null || lon == null || (lat === 0 && lon === 0)) ? (
                    <Alert severity="info">
                        Set the project location (latitude/longitude) on the Projects page to load weather data.
                        Weather data is used to calculate ambient temperature profiles for optimization.
                    </Alert>
                ) : weatherLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                        <CircularProgress size={24} sx={{ color: '#2196F3' }} />
                        <Typography variant="body2" color="textSecondary">
                            Loading weather data from NREL...
                        </Typography>
                    </Box>
                ) : weatherError ? (
                    <Alert severity="error">
                        Failed to load weather data: {weatherError}
                    </Alert>
                ) : weatherData ? (
                    <Box>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Design day temperature profile (hottest month average) for location ({lat?.toFixed(2)}, {lon?.toFixed(2)})
                        </Typography>
                        <Box sx={{ width: '100%', height: 250 }}>
                            <ResponsiveContainer>
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                    <XAxis
                                        dataKey="hour"
                                        stroke={vortxColors.stone}
                                        tick={{ fontSize: 11 }}
                                        interval={3}
                                    />
                                    <YAxis
                                        stroke={vortxColors.stone}
                                        tick={{ fontSize: 11 }}
                                        label={{ value: '°F', angle: -90, position: 'insideLeft', style: { fill: vortxColors.stone } }}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: isRetro ? '#242424' : '#fff',
                                            border: '1px solid rgba(33, 150, 243, 0.3)',
                                            borderRadius: 4,
                                        }}
                                        labelStyle={{ color: vortxColors.white }}
                                        itemStyle={{ color: '#2196F3' }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="temp"
                                        stroke="#2196F3"
                                        strokeWidth={2}
                                        dot={false}
                                        name="Ambient Temp"
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                            Peak: {Math.max(...weatherData.designDayTemps).toFixed(1)}°F |
                            Low: {Math.min(...weatherData.designDayTemps).toFixed(1)}°F |
                            Avg: {(weatherData.designDayTemps.reduce((a, b) => a + b, 0) / 24).toFixed(1)}°F
                        </Typography>
                    </Box>
                ) : null}
            </SectionCard>
        </Box>
    );
}
